import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Button,
  Typography,
  Box,
  ImageList,
  ImageListItem,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Card,
} from '@mui/material';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';


import CompletedPuzzle from '../../assets/images/puzzle-2.jpg';
import CompletedGreyPuzzle from '../../assets/images/grey-completed-puzzle.jpg';
import TrayAssortedPieces from '../../assets/images/puzzle-3.jpg';
import CompletedPuzzleVisibleDots from '../../assets/images/puzzle-4.jpg';
import GreenCompletedPuzzle from '../../assets/images/green-completed-puzzle.jpg';
import AssortedPieces from '../../assets/images/assorted-puzzle-pieces.jpg';
import BlackTray from '../../assets/images/black-puzzle-tray.jpg';
import GreenPieces from '../../assets/images/green-pieces-together.jpg';
import BlackAndBlueTrays from '../../assets/images/black-and-blue-trays.jpg';

import '../../App.css';

const fadeUp = (d = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut', delay: d } },
});

const itemData = [
  {
    img: CompletedPuzzle,
    title: 'Completed Braille skills puzzle',
    description:
      'White puzzle tray with red puzzle pieces. Puzzle pieces have raised geometric shapes and braille dots for easy tactile discrimination.',
  },
  {
    img: TrayAssortedPieces,
    title: 'Braille skills puzzle trays and assorted pieces',
    description: 'Tray and puzzle pieces both have braille dots.',
  },
  {
    img: BlackAndBlueTrays,
    title: 'Braille skills puzzle black and blue trays',
    description: 'Braille skills puzzle black and blue trays',
  },
  {
    img: CompletedPuzzleVisibleDots,
    title: 'Completed Braille skills puzzle with highly visible braille dots',
    description: 'Completed Braille skills puzzle with highly visible braille dots',
  },
  {
    img: BlackTray,
    title: 'Braille skills puzzle black tray',
    description: 'Braille skills puzzle black tray',
  },
  {
    img: AssortedPieces,
    title: 'Assorted Braille puzzle pieces',
    description: 'Multicolored collection of Braille puzzle pieces',
  },
  {
    img: GreenPieces,
    title: 'Braille skills puzzle completed green set',
    description: 'Green puzzle pieces with raised geometric shapes',
  },
  {
    img: GreenCompletedPuzzle,
    title: 'Set of green Braille puzzle pieces',
    description:
      'Green transparent puzzle pieces with raised geometric shapes and Braille dots laid out in two rows of five.',
  },
];

const ProductDetail = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const product = {
    id: 1,
    title: 'Braille Skills Puzzle',
    description:
      'This innovative educational tool is a tactile puzzle designed to introduce and reinforce Braille skills while engaging learners in an interactive, multi-sensory experience.',
    price: 59.99,
    weight: 16, // Weight in ounces
    img: CompletedGreyPuzzle,
  };

  const handleContactUs = () => {
    navigate('/contact');
    if (window.gtag) {
      window.gtag('event', 'contact_us_click', {
        event_category: 'Engagement',
        event_label: 'Product Detail Contact',
      });
    }
  };

  const handleBuyNow = () => {
    if (window.gtag) {
      window.gtag('event', 'purchase_intent', {
        event_category: 'E-commerce',
        event_label: 'Stripe Checkout',
        value: 59.99,
        currency: 'USD',
        item_id: 'braille-skills-puzzle',
        item_name: 'Braille Skills Puzzle',
      });
    }
    // The actual navigation to Stripe is handled by the href attribute
  };

  const handleClickOpen = (image) => {
    setSelectedImage(image);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedImage(null);
  };

  return (
    <Container disableGutters sx={{ maxWidth: '100% !important', p: 0 }}>
      <Helmet>
        <title>{`${product.title} | Amaze Puzzles`}</title>
        <meta
          name="description"
          content="Detailed view of the Braille Skills Puzzle. View images and learn more about this innovative product designed to enhance Braille literacy."
        />
      </Helmet>

      {/* Gradient page header */}
      <Box
        component={motion.header}
        {...fadeUp(0)}
        sx={{
          position: 'relative',
          color: 'white',
          textAlign: 'center',
          overflow: 'hidden',
          pt: { xs: 6, md: 9 },
          pb: { xs: 6, md: 9 },
          background: 'var(--hero-background-gradient)',
        }}
      >
        {/* Soft blobs */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            right: -90,
            width: 320,
            height: 320,
            bgcolor: 'var(--hero-blur-bg)',
            borderRadius: '50%',
            filter: 'blur(60px)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '12%',
            left: -110,
            width: 300,
            height: 300,
            bgcolor: 'var(--hero-blur-bg)',
            borderRadius: '50%',
            filter: 'blur(60px)',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', px: { xs: 3, sm: 5, md: 6 } }}>
          <Typography variant="h2" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
            {product.title}
          </Typography>
        </Container>
      </Box>

      {/* Single glass card containing EVERYTHING (CTA, intro, gallery, sections) */}
      <Container
        maxWidth="lg"
        sx={{ padding: '25px !important', px: { xs: 3, sm: 5, md: 6 }, py: { xs: 6, md: 10 } }}
      >
        <Card
          component={motion.section}
          {...fadeUp(0.05)}
          sx={{
            borderRadius: 4,
            border: '1px solid var(--card-border)',
            backgroundImage: 'var(--card-background-gradient)',
            backdropFilter: 'blur(8px)',
            boxShadow: 'var(--card-shadow)',
            p: { xs: 3, md: 4 },
          }}
        >
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center', mb: 4 }}>
            <Button
              variant="contained"
              sx={{
                borderRadius: 2,
                py: 1.25,
                px: 4,
                background: 'var(--button-gradient-primary)',
                '&:hover': { filter: 'brightness(0.95)' },
                fontSize: '1.1rem',
                fontWeight: 600,
              }}
              href="https://buy.stripe.com/aFa7sL8S16eAbY1aSwabK00"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleBuyNow}
              aria-label="Buy Braille Skills Puzzle - $59.99"
            >
              Buy Now - $59.99
            </Button>
            <Button
              variant="outlined"
              sx={{
                borderRadius: 2,
                py: 1.25,
                px: 4,
                borderColor: 'var(--button-outline-border)',
                color: 'var(--button-outline-text)',
                '&:hover': { 
                  borderColor: 'var(--button-outline-border-hover)', 
                  background: 'var(--button-outline-bg-hover)' 
                },
              }}
              onClick={handleContactUs}
              aria-label="Contact us about this product"
            >
              Contact Us
            </Button>
          </Box>

          {/* Product Description */}
          <Typography variant="h4" component="h2" sx={{ color: 'var(--primary-color)', mb: 3 }} gutterBottom>
            <strong>Braille Skills Puzzle — Product Details</strong>
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ color: 'var(--text-secondary)' }}>
            The Braille Skills Puzzle is a tactile, jigsaw-style puzzle designed to support Braille exposure, tactile discrimination, and spatial awareness through hands-on exploration.
          </Typography>

          {/* Masonry gallery (inside the same card) */}
          <ImageList variant="masonry" cols={3} gap={8} sx={{ my: 1 }}>
            {itemData.map((item, index) => (
              <Tooltip key={index} title={item.description} arrow placement="top">
                <ImageListItem
                  className="gallery-item"
                  onClick={() => handleClickOpen(item.img)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleClickOpen(item.img);
                  }}
                  aria-label={item.title}
                  sx={{
                    cursor: 'zoom-in',
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: 'var(--card-shadow)',
                  }}
                >
                  <img
                    src={`${item.img}?w=248&fit=crop&auto=format`}
                    srcSet={`${item.img}?w=248&fit=crop&auto=format&dpr=2 2x`}
                    alt={item.title}
                    loading="lazy"
                    style={{ width: '100%', height: '100%', display: 'block' }}
                  />
                </ImageListItem>
              </Tooltip>
            ))}
          </ImageList>

          <Typography variant="h5" component="h2" sx={{ color: 'var(--primary-color)', mt: 4 }} gutterBottom>
            <strong>What's Included</strong>
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ color: 'var(--text-secondary)' }}>
            <strong>10 uniquely shaped puzzle pieces, each with:</strong>
          </Typography>
          <Typography variant="body1" component="ul" sx={{ pl: 2, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
            <li>A distinct contour that fits only one location in the tray</li>
            <li>Raised Braille numerals</li>
            <li>A central geometric symbol representing numeracy through quantity</li>
          </Typography>

          <Typography variant="body1" paragraph sx={{ color: 'var(--text-secondary)', mt: 2 }}>
            <strong>Durable puzzle tray with:</strong>
          </Typography>
          <Typography variant="body1" component="ul" sx={{ pl: 2, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
            <li>Raised guides for tactile alignment</li>
            <li>Matching Braille numerals centered in each placement space</li>
          </Typography>

          <Typography variant="h5" component="h2" sx={{ color: 'var(--primary-color)', mt: 4 }} gutterBottom>
            <strong>How It Works</strong>
          </Typography>
          <Typography variant="body1" paragraph sx={{ color: 'var(--text-secondary)' }}>
            The puzzle can be solved through shape matching, Braille-to-Braille matching, or simple tactile exploration. Braille supports discovery but is not required for completion, allowing users to engage at their own level and progress naturally over time.
          </Typography>

          <Typography variant="h5" component="h2" sx={{ color: 'var(--primary-color)', mt: 4 }} gutterBottom>
            <strong>Why It Matters</strong>
          </Typography>
          <Typography variant="body1" component="ul" sx={{ pl: 2, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
            <li>Encourages active tactile exploration and fine motor engagement</li>
            <li>Supports spatial reasoning and orientation skills</li>
            <li>Provides repeated, low-pressure exposure to Braille numerals</li>
            <li>Integrates numeracy through tactile symbology</li>
            <li>Designed for independent, self-directed use</li>
          </Typography>

          <Typography variant="body1" paragraph sx={{ mt: 3, color: 'var(--text-secondary)' }}>
            The Braille Skills Puzzle is intentionally challenging without being frustrating, making it suitable for a wide range of ages and experience levels. It functions equally well as an instructional support, a tactile pastime, or a skill-maintenance tool.
          </Typography>

          <Typography variant="body1" paragraph sx={{ color: 'var(--text-secondary)', fontStyle: 'italic', textAlign: 'center', mt: 3 }}>
            <strong>It's not a toy… it's a puzzle.</strong>
          </Typography>

          {/* Donation Information */}
          <Box sx={{ 
            mt: 4, 
            p: 3, 
            borderRadius: 2, 
            border: '1px solid var(--card-border)',
            backgroundImage: 'var(--card-background-gradient)',
            textAlign: 'center'
          }}>
            <Typography variant="h6" sx={{ color: 'var(--primary-color)', mb: 2, fontWeight: 600 }}>
              Want to donate a puzzle to someone in need?
            </Typography>
            <Typography variant="body1" sx={{ color: 'var(--text-secondary)', mb: 2 }}>
              Visit the Dual Tools Initiative to learn about donation opportunities and help make Braille learning accessible to more people.
            </Typography>
            <Button
              variant="outlined"
              href="https://dualtoolsinitiative.org/donate"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                borderRadius: 2,
                py: 1,
                px: 3,
                borderColor: 'var(--button-outline-border)',
                color: 'var(--button-outline-text)',
                '&:hover': { 
                  borderColor: 'var(--button-outline-border-hover)', 
                  background: 'var(--button-outline-bg-hover)' 
                },
              }}
              aria-label="Visit Dual Tools Initiative donation page"
            >
              Learn About Donations
            </Button>
          </Box>
        </Card>
      </Container>

      {/* Lightbox */}
      <Dialog open={open} onClose={handleClose} maxWidth="lg">
        <DialogTitle sx={{ pr: 6 }}>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <img src={selectedImage} alt="Selected" style={{ width: '100%', height: 'auto', display: 'block' }} />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default ProductDetail;
